import axios from 'axios';

const BASE = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BASE}/api` });

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Each browser tab keeps its own session (sessionStorage) so two accounts can be open side by side;
// localStorage only seeds a brand-new tab with the most recent sign-in.
export const getToken = () => sessionStorage.getItem('bm_token') || localStorage.getItem('bm_token');
export const setToken = (t) => { sessionStorage.setItem('bm_token', t); localStorage.setItem('bm_token', t); };
export const clearToken = () => { sessionStorage.removeItem('bm_token'); localStorage.removeItem('bm_token'); };

export const AuthAPI = {
  register: (data) => api.post('/auth/register', data).then((r) => r.data),
  login: (data) => api.post('/auth/login', data).then((r) => r.data),
  me: () => api.get('/auth/me').then((r) => r.data),
  forgot: (email) => api.post('/auth/forgot-password', { email }).then((r) => r.data),
  reset: (token, password) => api.post('/auth/reset-password', { token, password }).then((r) => r.data),
  changePassword: (current_password, new_password) => api.post('/auth/change-password', { current_password, new_password }).then((r) => r.data),
};

export const errText = (e, fallback = 'Something went wrong.') => {
  const d = e?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(' ');
  return d.msg || String(d);
};

export const fileUrl = (src) => (src && src.startsWith('/') ? `${BASE}${src}` : src);

export const API = {
  songs: () => api.get('/songs').then((r) => r.data),
  readSite: (url) => api.post('/ai/read-site', { url }).then((r) => r.data),
  createRequest: (payload) => api.post('/requests', payload).then((r) => r.data),
  listRequests: () => api.get('/requests').then((r) => r.data),
  getRequest: (code) => api.get(`/requests/${code}`).then((r) => r.data),
  patchRequest: (code, payload) => api.patch(`/requests/${code}`, payload).then((r) => r.data),
  checkout: (payload) => api.post('/payments/checkout', { ...payload, origin_url: window.location.origin }).then((r) => r.data),
  paymentStatus: (sessionId) => api.get(`/payments/status/${sessionId}`).then((r) => r.data),
  upload: (file, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress: (e) => onProgress && e.total && onProgress(Math.round((e.loaded / e.total) * 100)) }).then((r) => r.data);
  },
  sampleSong: () => api.get('/settings/sample-song').then((r) => r.data),
  setSampleSong: (payload) => api.put('/settings/sample-song', payload).then((r) => r.data),
  studioSettings: () => api.get('/settings/studio').then((r) => r.data),
  saveStudioSettings: (payload) => api.put('/settings/studio', payload).then((r) => r.data),
  customers: () => api.get('/studio/customers').then((r) => r.data),
  plans: () => api.get('/plans').then((r) => r.data),
  mySubscriptions: () => api.get('/subscriptions/me').then((r) => r.data),
  studioSubscriptions: () => api.get('/studio/subscriptions').then((r) => r.data),
  billingPortal: () => api.post('/subscriptions/portal', { origin_url: window.location.origin }).then((r) => r.data),
  posts: (owner) => api.get('/posts', { params: owner ? { owner } : {} }).then((r) => r.data),
  createPost: (payload) => api.post('/posts', payload).then((r) => r.data),
  patchPost: (id, payload) => api.patch(`/posts/${id}`, payload).then((r) => r.data),
  deletePost: (id) => api.delete(`/posts/${id}`).then((r) => r.data),
  reviewPost: (id, decision, feedback) => api.post(`/posts/${id}/review`, { decision, feedback }).then((r) => r.data),
  createCustomer: (payload) => api.post('/studio/customers', payload).then((r) => r.data),
  share: (code) => api.get(`/share/${code}`).then((r) => r.data),
  setShare: (code, enabled) => api.patch(`/requests/${code}/share`, { share_enabled: enabled }).then((r) => r.data),
  deleteRequest: (code) => api.delete(`/requests/${code}`).then((r) => r.data),
  setFeatured: (code, versionId) => api.patch(`/requests/${code}/featured`, { version_id: versionId }).then((r) => r.data),
  pricing: () => api.get('/settings/pricing').then((r) => r.data),
  launch: (code) => api.get(`/requests/${code}/launch`).then((r) => r.data),
  launchVideo: (code, versionId) => api.post(`/requests/${code}/launch/video`, { version_id: versionId }).then((r) => r.data),
  deleteLaunchVideo: (code, vid) => api.delete(`/requests/${code}/launch/video/${vid}`).then((r) => r.data),
  launchCaptions: (code, versionId) => api.post(`/requests/${code}/launch/captions`, { version_id: versionId }).then((r) => r.data),
  saveLaunchCaptions: (code, captions) => api.patch(`/requests/${code}/launch/captions`, { captions }).then((r) => r.data),
  setLaunchPhotos: (code, photos) => api.put(`/requests/${code}/launch/photos`, { photos }).then((r) => r.data),
  launchAiVideo: (code, versionId) => api.post(`/requests/${code}/launch/ai-video`, { version_id: versionId }).then((r) => r.data),
  release: (code) => api.get(`/requests/${code}/release`).then((r) => r.data),
  requestRelease: (code, payload) => api.post(`/requests/${code}/release`, payload).then((r) => r.data),
  updateRelease: (code, payload) => api.patch(`/requests/${code}/release`, payload).then((r) => r.data),
  downloadLogoKit: async (code, business) => {
    const r = await api.get(`/requests/${code}/logo-kit`, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url; a.download = `${(business || code).toLowerCase().replace(/[^a-z0-9]+/g, '-')}-social-logo-kit.zip`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  },
  createMarketing: (payload) => api.post('/marketing', payload).then((r) => r.data),
  listMarketing: () => api.get('/marketing').then((r) => r.data),
  quoteMarketing: (code, quote) => api.patch(`/marketing/${code}/quote`, { quote }).then((r) => r.data),
  stats: () => api.get('/studio/stats').then((r) => r.data),
  payments: () => api.get('/payments').then((r) => r.data),
  receipts: () => api.get('/receipts').then((r) => r.data),
};

export default api;
